import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // Clear any invalid session data
          await handleSignOut();
        } else {
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error('Unexpected error during auth initialization:', err);
        await handleSignOut();
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        await handleSignOut();
      } else if (event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
      } else {
        setUser(session?.user ?? null);
      }
    });

    return () => {
      try {
        subscription.unsubscribe();
      } catch (err) {
        console.error('Error unsubscribing from auth changes:', err);
      }
    };
  }, []);

  const handleSignOut = async () => {
    try {
      // Clear user state
      setUser(null);
      
      // Clear all Supabase local storage items
      Object.keys(localStorage)
        .filter(key => key.startsWith('sb-'))
        .forEach(key => localStorage.removeItem(key));
      
      // Clear any other auth-related storage
      localStorage.removeItem('supabase.auth.token');
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  };

  const signOut = async () => {
    try {
      // Clear local state and storage first
      await handleSignOut();
      
      // Then sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // Ensure user is still signed out locally even if Supabase call fails
      await handleSignOut();
    }
  };

  const value = {
    user,
    loading,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};